import React, {ReactNode, ReactElement} from 'react'
import {
    AccordionItem as ChackraAccordionItem,
    AccordionButton,
    AccordionPanel,
    useMultiStyleConfig,
    AccordionButtonProps,
    VisuallyHidden,
    Box,
} from '@chakra-ui/react'
import {Icon} from '_uiComponents'

interface AccordionItemProps {
    title: string | ReactNode
    children: ReactNode
    variant?: 'default' | 'bgGrey'
    expandIcon?: ReactElement
    collapseIcon?: ReactElement
    screenReaderCategoryLabelToExpand?: string
    screenReaderCategoryLabelExpanded?: string
    onItemClick?: (isExpanded: boolean) => void
}

/**
 * @name AccordionItem
 *
 * @component
 *
 * @description
 * Renders an accordion item using Chakra UI's AccordionItem.
 *
 * @param {Object} props - The properties object.
 * @param {string | ReactNode} props.title - The title of the accordion button.
 * @param {ReactNode} props.children - Content showed in the accordion panel.
 * @param {ReactElement | undefined} props.expandIcon - The icon to be displayed when the item is expandable.
 * @param {ReactElement | undefined} props.collapseIcon - The icon to be displayed when the item is collapseable.
 * @param {string} [props.screenReaderCategoryLabelToExpand] - Label to show on screen readers for `toExpand` behaviour.
 * @param {string} [props.screenReaderCategoryLabelExpanded] - Label to show on screen readers for `expanded` behaviour.
 * @param {(isExpanded: boolean) => void} [props.onItemClick] - Accordion item click callback.
 *
 * @returns {JSX.Element} The rendered `AccordionItem` component.
 */
const AccordionItem = ({
    title,
    children,
    variant = 'default',
    expandIcon,
    collapseIcon,
    screenReaderCategoryLabelToExpand,
    screenReaderCategoryLabelExpanded,
    onItemClick,
}: AccordionItemProps): JSX.Element => {
    const styles = useMultiStyleConfig('FiltersList', {variant}) as Record<string, AccordionButtonProps>

    const renderCustomIcon = expandIcon && collapseIcon

    /**
     * Title must be aria hidden only when screen reader labels are passed via props.
     */
    const isTitleAriaHidden = !!screenReaderCategoryLabelToExpand && !!screenReaderCategoryLabelExpanded

    return (
        <ChackraAccordionItem data-testid="accordion-item">
            {({isExpanded}) => (
                <>
                    <AccordionButton {...styles.accordionButton} data-testid="accordion-button" onClick={() => onItemClick?.(isExpanded)}>
                        <Box aria-hidden={isTitleAriaHidden}>{title}</Box>

                        {isTitleAriaHidden && (
                            <VisuallyHidden>{!isExpanded ? screenReaderCategoryLabelToExpand : screenReaderCategoryLabelExpanded}</VisuallyHidden>
                        )}

                        {renderCustomIcon ? <>{isExpanded ? collapseIcon : expandIcon}</> : <Icon name="IconChevronDown" size="xs" aria-hidden />}
                    </AccordionButton>
                    <AccordionPanel>{children}</AccordionPanel>
                </>
            )}
        </ChackraAccordionItem>
    )
}

export {AccordionItem}
